import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/Select';
import { Loader2 } from 'lucide-react';
import { useUsersApi } from '../apis/users-apis';
import { useToast } from './ui/Toast';
import { UserData } from '../types';

interface OwnerSelectionProps {
  currentOwnerId: string;
  onOwnerChange: (newOwnerId: string) => void;
  isReadOnly?: boolean;
}

interface UserInfo {
  userId: string;
  email: string;
}

const OwnerSelection: React.FC<OwnerSelectionProps> = ({
  currentOwnerId,
  onOwnerChange,
  isReadOnly = false
}) => {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { addToast } = useToast();
  const { getUsers } = useUsersApi();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersData = await getUsers();
        const activeUsers = usersData.filter((user: UserData) => user.isActive);
        setUsers(activeUsers.map((user: UserData) => ({
          userId: user.userId,
          email: user.email
        })));
      } catch (error) {
        console.error('Error fetching users:', error);
        addToast({
          title: "Error",
          description: "Failed to fetch users",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [addToast]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }

  const currentOwner = users.find(user => user.userId === currentOwnerId);

  return (
    <Card className="mt-4">
      <CardContent className="pt-6">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Owner
          </label>
          {isReadOnly ? (
            <div className="text-gray-900 py-2">
              {currentOwner?.email || 'Unknown Owner'}
            </div>
          ) : (
            <Select
              value={currentOwnerId}
              onValueChange={onOwnerChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select owner" />
              </SelectTrigger>
              <SelectContent>
                {users.map(user => (
                  <SelectItem key={user.userId} value={user.userId}>
                    {user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default OwnerSelection;